import { AppUpdateNotifier } from "@/components/Common/AppUpdateNotifier";
import ErrorBoundary from "@/components/Common/ErrorBoundary";
import Loading from "@/components/Common/Loading";
import ProductionWarningBanner from "@/components/Common/ProductionWarningBanner";
import { Toaster } from "@/components/ui/sonner";
import { ShortcutProvider } from "@/context/ShortcutContext";
import Integrations from "@/Integrations";
import { OverrideProvider } from "@/lib/override";
import PluginEngine from "@/PluginEngine";
import AuthUserProvider from "@/Providers/AuthUserProvider";
import PublicRouter from "@/Routers/PublicRouter";
import { displayMansoqConsoleArt } from "@/Utils/consoleArt";
import queryClient from "@/Utils/request/queryClient";
import mansoqConfig from "@mansoqConfig";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useLocationChange } from "raviger";
import { lazy, Suspense, useEffect } from "react";
import { PubSubProvider } from "./Utils/pubsubContext";

const PatientRouter = lazy(() => import("@/Routers/PatientRouter"));
const AppRouter = lazy(() => import("@/Routers/AppRouter"));
const MansoqRobot = lazy(() =>
  import("@/components/MansoqRobot/MansoqRobot").then((m) => ({
    default: m.MansoqRobot,
  })),
);

const ScrollToTop = () => {
  useLocationChange(() => {
    window.scrollTo(0, 0);
  });

  return null;
};

const App = () => {
  useEffect(() => {
    displayMansoqConsoleArt();
  }, []);

  return (
    <>
      <ProductionWarningBanner />
      <QueryClientProvider client={queryClient}>
        <ScrollToTop />
        <Suspense fallback={<Loading />}>
          <PubSubProvider>
            <ShortcutProvider>
              <PluginEngine>
                <OverrideProvider>
                  <AuthUserProvider
                    unauthorized={<PublicRouter />}
                    otpAuthorized={
                      <Suspense fallback={<Loading />}>
                        <PatientRouter />
                      </Suspense>
                    }
                  >
                    <Suspense fallback={<Loading />}>
                      <AppRouter />
                    </Suspense>
                    {/* Outside the router's Suspense so a lazily loaded page
                        never unmounts the robot mid-conversation. */}
                    <ErrorBoundary fallback={null}>
                      <Suspense fallback={null}>
                        <MansoqRobot />
                      </Suspense>
                    </ErrorBoundary>
                  </AuthUserProvider>
                </OverrideProvider>
                <Toaster
                  position={mansoqConfig.toastPosition}
                  theme="light"
                  richColors
                  expand
                  // For `richColors` to work, pass at-least an empty object.
                  // Refer: https://github.com/shadcn-ui/ui/issues/2234.
                  toastOptions={{}}
                  closeButton
                />
                <AppUpdateNotifier />
              </PluginEngine>
            </ShortcutProvider>
          </PubSubProvider>
        </Suspense>

        {/* Devtools are not included in production builds by default */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
      <Integrations.Sentry disabled={!import.meta.env.PROD} />
    </>
  );
};

export default App;
